import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }


  /**
   * Finds the rendered cart row for a Shopify cart line number.
   * Hidden warranty lines are not rendered on the cart page, so array indexes are not reliable.
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRow(line) {
    return this.refs.cartItemRows.find((row) => {
      const input = row.querySelector('input[data-cart-line]');
      return Number(input?.getAttribute('data-cart-line') || 0) === Number(line || 0);
    });
  }

  /**
   * Finds the rendered quantity selector for a Shopify cart line number.
   * @param {number} line
   * @returns {HTMLElement | undefined}
   */
  #getQuantitySelector(line) {
    return this.refs.quantitySelectors.find((selector) => {
      const input = selector.querySelector('input[data-cart-line]');
      return Number(input?.getAttribute('data-cart-line') || 0) === Number(line || 0);
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, only the drawer should switch optimistically
    // to the empty state. On the full cart page we wait for the server response so the page
    // never briefly mixes the empty template with stale line-item markup.
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart) {
      this.#syncWarrantyMainCartCount(0);

      if (!this.isDrawer) {
        document.documentElement.classList.add('warranty-cart-clearing-last-page-item');
      }

      if (this.isDrawer && template instanceof HTMLTemplateElement) {
        const clone = document.importNode(template.content, true);

        startViewTransition(() => {
          this.replaceChildren(clone);
        }, ['empty-cart-drawer']);

        return;
      }

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    this.#optimisticallySyncWarrantyMainCartCount(line, quantity);

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          window.__warrantyNormalizeCartBubbleCount?.(true);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const actualLineQuantity = Number(parsedResponseText.items?.[line - 1]?.quantity);
        const hasAdjustedQuantity = Number.isFinite(actualLineQuantity) && actualLineQuantity !== quantity;

        if (hasAdjustedQuantity && quantity > 0) {
          this.#injectAvailabilityWarning(newSectionHTML, line, actualLineQuantity);
        }

        const mainCountFromResponse = this.#getMainCartCountFromResponse(parsedResponseText);
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const fallbackCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
        const newCartItemCount = mainCountFromResponse ?? fallbackCartItemCount;

        this.#syncWarrantyMainCartCount(newCartItemCount);

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        const sectionHtml = newSectionHTML.body.innerHTML;
        const nextSections = {
          ...(parsedResponseText.sections || {}),
          [this.sectionId]: sectionHtml,
        };

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: nextSections,
          })
        );

        morphSection(this.sectionId, sectionHtml, this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
        this.#scheduleAuthoritativeBubbleRefresh();
      })
      .catch((error) => {
        console.error(error);
        window.__warrantyNormalizeCartBubbleCount?.(true);
      })
      .finally(() => {
        document.documentElement.classList.remove('warranty-cart-clearing-last-page-item');
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelector(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };



  /**
   * Injects a stock-availability warning into the incoming section HTML.
   * Shopify can clamp the requested quantity without returning parsedResponseText.errors.
   * @param {Document} sectionDocument
   * @param {number} line
   * @param {number} actualQuantity
   */
  #injectAvailabilityWarning(sectionDocument, line, actualQuantity) {
    if (!(sectionDocument instanceof Document)) return;

    const cartItemError = sectionDocument.querySelector(`[ref="cartItemError-${line}"]`);
    const cartItemErrorContainer = sectionDocument.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
    const quantityInput = sectionDocument.querySelector(`input[data-cart-line="${line}"]`);

    const safeQuantity = Math.max(0, Number(actualQuantity || 0));

    if (quantityInput instanceof HTMLInputElement) {
      const nextValue = String(safeQuantity);
      quantityInput.value = nextValue;
      quantityInput.defaultValue = nextValue;
      quantityInput.setAttribute('value', nextValue);
      quantityInput.setAttribute('data-cart-quantity', nextValue);
    }

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = `Aufgrund der Verfügbarkeit wurden nur ${safeQuantity} Artikel zu deinem Warenkorb hinzugefügt.`;
    cartItemErrorContainer.classList.remove('hidden');
  }

  /**
   * Returns the authoritative non-warranty cart count from the Ajax cart response.
   * @param {Object} cartResponse
   * @returns {number | null}
   */
  #getMainCartCountFromResponse(cartResponse) {
    if (!Array.isArray(cartResponse?.items)) return null;

    const total = cartResponse.items.reduce((sum, item) => {
      const props = item?.properties || {};
      const isWarranty = String(props._is_warranty || '') === '1';
      return sum + (isWarranty ? 0 : Math.max(0, Number(item?.quantity || 0)));
    }, 0);

    return Math.max(0, Number(total || 0));
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (typeof event.detail?.itemCount === 'number') {
      this.#syncWarrantyMainCartCount(event.detail.itemCount);
    }

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };


  /**
   * Returns the current main-product cart count shown by this section.
   * @returns {number}
   */
  #getCurrentMainCartCount() {
    const localCount = this.querySelector('[ref="cartItemCount"]')?.textContent;
    const parsedLocal = Number.parseInt(localCount || '', 10);
    if (Number.isFinite(parsedLocal)) return Math.max(0, parsedLocal);

    try {
      const persisted = sessionStorage.getItem('warranty:main-cart-count');
      const parsedPersisted = Number.parseInt(persisted || '', 10);
      if (Number.isFinite(parsedPersisted)) return Math.max(0, parsedPersisted);
    } catch (_) {
      // ignore
    }

    const bubbleText = document.querySelector('.cart-bubble__text-count, .cart-bubble__count, [data-cart-bubble-count]')?.textContent;
    const parsedBubble = Number.parseInt((bubbleText || '').replace(/\D+/g, ''), 10);
    return Number.isFinite(parsedBubble) ? Math.max(0, parsedBubble) : 0;
  }

  /**
   * Returns the currently rendered quantity of a cart line.
   * @param {number} line
   * @returns {number}
   */
  #getRenderedLineQuantity(line) {
    const row = this.#getCartItemRow(line);
    if (!row) return 0;

    const quantityInput = row.querySelector('quantity-selector-component input, .quantity-selector input, input[name="updates[]"], input[data-cart-quantity]');
    const raw = quantityInput?.value || quantityInput?.getAttribute('value') || quantityInput?.getAttribute('data-cart-quantity') || '0';
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  }

  /**
   * Updates the warranty-adjusted bubble count immediately from the user's intent,
   * so fast reloads do not briefly show a stale server-rendered count.
   * @param {number} line
   * @param {number} nextQuantity
   */
  #optimisticallySyncWarrantyMainCartCount(line, nextQuantity) {
    const row = this.#getCartItemRow(line);
    if (!row) return;
    if (row.dataset.isWarranty === '1') return;

    const currentCount = this.#getCurrentMainCartCount();
    const currentLineQuantity = this.#getRenderedLineQuantity(line);
    const safeNextQuantity = Math.max(0, Number(nextQuantity || 0));
    const optimisticCount = Math.max(0, currentCount - currentLineQuantity + safeNextQuantity);

    this.#syncWarrantyMainCartCount(optimisticCount);
  }

  /**
   * Keeps the warranty-adjusted cart bubble count in sync across page navigations.
   * @param {number} count
   */
  #syncWarrantyMainCartCount(count) {
    const safeCount = Math.max(0, Number(count || 0));

    try {
      sessionStorage.setItem('warranty:main-cart-count', String(safeCount));
    } catch (_) {
      // ignore
    }

    window.__warrantyMainCartCount = safeCount;

    const displayCount = safeCount > 99 ? '99+' : String(safeCount);

    document
      .querySelectorAll('.cart-bubble__text-count, .cart-bubble__count, [data-cart-bubble-count]')
      .forEach((el) => {
        el.textContent = displayCount;
        el.classList.toggle('hidden', safeCount === 0);

        const bubble = el.closest('.cart-bubble');
        if (bubble) bubble.classList.toggle('visually-hidden', safeCount === 0);
      });

    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', safeCount === 0);
    });
  }

  #getSharedBubbleRefreshState() {
    if (!window.__gaslandBubbleRefreshState) {
      window.__gaslandBubbleRefreshState = { inFlight: null, token: 0 };
    }
    return window.__gaslandBubbleRefreshState;
  }

  #scheduleAuthoritativeBubbleRefresh() {
    const state = this.#getSharedBubbleRefreshState();
    const token = ++state.token;

    const run = () => {
      if (state.token !== token) return;
      this.#refreshWarrantyMainCartCountFromServer();
    };

    run();
    window.setTimeout(run, 120);
    window.setTimeout(run, 320);
  }

  #refreshWarrantyMainCartCountFromServer() {
    const state = this.#getSharedBubbleRefreshState();
    if (state.inFlight) return state.inFlight;

    state.inFlight = fetch(Theme.routes.cart_url + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) throw new Error('cart.js failed');
        return response.json();
      })
      .then((cart) => {
        const count = Array.isArray(cart?.items)
          ? cart.items.reduce((sum, item) => {
              const props = item?.properties || {};
              const isWarranty = String(props._is_warranty || '') === '1';
              return sum + (isWarranty ? 0 : Math.max(0, Number(item?.quantity || 0)));
            }, 0)
          : 0;

        this.#syncWarrantyMainCartCount(count);
      })
      .catch(() => {})
      .finally(() => {
        state.inFlight = null;
      });

    return state.inFlight;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        const safeQuantity = Math.max(0, Number(item.quantity || 0)).toString();
        input.setAttribute('data-cart-quantity', safeQuantity);
        input.setAttribute('value', safeQuantity);
        input.defaultValue = safeQuantity;
        input.value = safeQuantity;

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
